import { useSyncExternalStore } from "react";
import { errorSentence } from "../../lib/errorWording";

// A tiny global toast store (ported from Summit Engineering's components\toast\toast.ts, release 1.5.0 polish):
// any module calls `toast.success("Saved")` and the one <Toaster /> mounted by the app shell renders it. Every toast
// carries an icon AND words (status never by colour alone), errors stay longer, and the region is announced to screen
// readers. A toast may carry one follow-up action, used for "Undo" (O-R67).

export type ToastTone = "success" | "error" | "info";

export interface ToastAction {
  label: string;
  /** Spoken name when the label alone is ambiguous ("Undo unpinning Dispatch"). */
  ariaLabel?: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  detail?: string;
  /** Milliseconds until it dismisses itself; 0 = stays until dismissed. */
  durationMs: number;
  action?: ToastAction;
}

export interface ToastOptions {
  detail?: string;
  durationMs?: number;
  action?: ToastAction;
}

const DEFAULT_DURATION: Record<ToastTone, number> = { success: 4000, info: 5000, error: 8000 };
const MAX_VISIBLE = 4;

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function push(tone: ToastTone, title: string, options?: ToastOptions): number {
  const id = nextId++;
  items = [...items, { id, tone, title, detail: options?.detail, durationMs: options?.durationMs ?? DEFAULT_DURATION[tone], action: options?.action }].slice(-MAX_VISIBLE);
  emit();
  return id;
}

export function dismissToast(id: number): void {
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
}

/** Clears every toast (tests, sign-out). */
export function clearToasts(): void {
  items = [];
  emit();
}

export const toast = {
  success: (title: string, options?: ToastOptions) => push("success", title, options),
  info: (title: string, options?: ToastOptions) => push("info", title, options),
  error: (title: string, options?: ToastOptions) => push("error", title, options),
  /** An error toast in the plain O-R68 wording, led by what the person tried. */
  fromError: (lead: string, error: unknown) => push("error", lead, { detail: errorSentence(error) }),
  dismiss: dismissToast
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    subscribe,
    () => items,
    () => items
  );
}
